import AsyncLock from 'async-lock';
import ky from 'ky';
import loki from 'lokijs';
import Config, { type BrainAtlasType, BrainAtlasTypes } from '../../config';
import type { LokiSearchFilter } from './types';

export interface Index {
    id: number;
    title: string;
    numOfItems: number;
    parentId: number;
    weight: number;
}

export const INDEX_ID_ROOT = 1;
export const INDEX_ID_PUBLIC = 3;

interface IndexData {
    id: number;
    title: string;
    num_of_items: number;
    children: IndexData[];
}
type IndexesData = IndexData[];

export type GetCallbackFunc = (index: Index | null) => void;
export type SearchCallbackFunc = (indexes: Index[]) => void;
export type CountCallbackFunc = (count: number) => void;

type IndexLoadCallbackFunc = (indexes: Collection<Index>) => void;

class IndexUtil {
    private database: loki;
    private indexes: Map<BrainAtlasType, Collection<Index>>;
    private loading: Map<BrainAtlasType, boolean>;
    private callbacks: Map<BrainAtlasType, IndexLoadCallbackFunc[]>;

    constructor() {
        this.database = new loki('database', { env: 'BROWSER' });
        this.indexes = new Map<BrainAtlasType, Collection<Index>>();
        this.loading = new Map<BrainAtlasType, boolean>();
        this.callbacks = new Map<BrainAtlasType, IndexLoadCallbackFunc[]>();
        this.load();
    }

    load(): void {
        BrainAtlasTypes.forEach((type) => {
            const collection = this.database.addCollection<Index>(`${type}_indexes`);
            this.indexes.set(type, collection);
            this.loading.set(type, true);
            this.callbacks.set(type, []);
            ky.get(new URL(`${Config.BASE_URL}/${type}/modules/xoonips/tree.json`, window.location.href))
                .json<IndexData[]>()
                .then((data) => {
                    const store = (indexesData: IndexesData, parentId: number) => {
                        indexesData.forEach((indexData: IndexData, idx) => {
                            const entry: Index = {
                                id: indexData.id,
                                title: indexData.title,
                                numOfItems: indexData.num_of_items,
                                parentId: parentId,
                                weight: idx,
                            };
                            collection.insert(entry);
                            store(indexData.children, indexData.id);
                        });
                    };
                    store(data, INDEX_ID_ROOT);
                    const lock = new AsyncLock();
                    lock.acquire(
                        `${type}_indexes`,
                        () => {
                            this.loading.set(type, false);
                            const callbacks = this.callbacks.get(type);
                            if (typeof callbacks !== 'undefined') {
                                callbacks.forEach((callback) => {
                                    callback(collection);
                                });
                                this.callbacks.set(type, []);
                            }
                        },
                        () => {
                            // ignore
                        }
                    );
                })
                .catch(() => {
                    // ignore
                });
        });
    }

    registerIndexLoadCallback(type: BrainAtlasType, func: IndexLoadCallbackFunc): void {
        const lock = new AsyncLock();
        lock.acquire(
            `${type}_indexes`,
            () => {
                if (this.loading.get(type)) {
                    const callbacks = this.callbacks.get(type);
                    if (typeof callbacks !== 'undefined') {
                        callbacks.push(func);
                    }
                } else {
                    const collection = this.indexes.get(type);
                    if (typeof collection !== 'undefined') {
                        func(collection);
                    }
                }
            },
            () => {
                // ignore
            }
        );
    }

    getUrl(type: BrainAtlasType, id: number): string {
        return `/${type}/modules/xoonips/listitem.php?index_id=${String(id)}`;
    }

    get(type: BrainAtlasType, indexId: number, func: GetCallbackFunc): void {
        this.registerIndexLoadCallback(type, (indexes) => {
            const filter: LokiSearchFilter = {
                id: indexId,
            };
            const index = indexes.findOne(filter);
            func(index);
        });
    }

    getChildren(type: BrainAtlasType, indexId: number, func: SearchCallbackFunc): void {
        this.registerIndexLoadCallback(type, (indexes) => {
            const filter: LokiSearchFilter = {
                parentId: indexId,
            };
            const children = indexes.chain().find(filter).simplesort('weight').data();
            func(children);
        });
    }

    countChildren(type: BrainAtlasType, indexId: number, func: CountCallbackFunc): void {
        this.registerIndexLoadCallback(type, (indexes) => {
            const filter: LokiSearchFilter = {
                parentId: indexId,
            };
            const count = indexes.count(filter);
            func(count);
        });
    }

    getParents(type: BrainAtlasType, parentId: number, func: SearchCallbackFunc): void {
        this.registerIndexLoadCallback(type, (indexes) => {
            const parents: Index[] = [];
            const loop = (parentId: number) => {
                if (parentId !== INDEX_ID_ROOT) {
                    const parent = indexes.findOne({ id: parentId });
                    if (parent !== null) {
                        loop(parent.parentId);
                        parents.push(parent);
                    }
                }
            };
            loop(parentId);
            func(parents);
        });
    }
}

export default new IndexUtil();
